import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { settlementReportsApi } from '../../api/endpoints';
import { SettlementReportDetail, SettlementTransaction } from '../../types';
import {
  FiHash, FiDollarSign, FiCalendar, FiDownload,
  FiCheckCircle, FiDatabase, FiArrowLeft, FiRefreshCw,
  FiTrendingUp, FiTrendingDown, FiPackage, FiAlertCircle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import CountryFlag from '../../components/common/CountryFlag';

/* ------------------------------------------------------------------ */
/*  Marketplace name → ISO country code                                */
/*  Mirrors the table in SettlementReportsPage so the detail page can  */
/*  render the same Pan-EU marketplace pill-strip rather than dumping  */
/*  raw IDs into a label.                                              */
/* ------------------------------------------------------------------ */

const marketplaceToCountry: Record<string, string> = {
  'amazon.de': 'DE', 'amazon.fr': 'FR', 'amazon.it': 'IT', 'amazon.es': 'ES',
  'amazon.co.uk': 'GB', 'amazon.com': 'US', 'amazon.pl': 'PL', 'amazon.se': 'SE',
  'amazon.nl': 'NL', 'amazon.com.be': 'BE', 'amazon.co.jp': 'JP', 'amazon.ca': 'CA',
  'amazon.com.au': 'AU', 'amazon.com.mx': 'MX', 'amazon.com.tr': 'TR',
  // Marketplace IDs as fallback (Pan-EU reports come back with raw IDs)
  'A1PA6795UKMFR9': 'DE', 'A13V1IB3VIYZZH': 'FR', 'APJ6JRA9NG5V4': 'IT',
  'A1RKKUPIHCS9HS': 'ES', 'A1F83G8C2ARO7P': 'GB', 'A1805IZSGTT6HS': 'NL',
  'A1C3SOZRARQ6R3': 'PL', 'A2NODRKZP88ZB9': 'SE',
  // Extras seen in EU panEU reports
  'AMEN7PMS3EDWL': 'BE', 'A33AVAJ2PDY3EV': 'TR',
};

const getMarketplaceCountry = (marketplace: string | null | undefined): string | null => {
  if (!marketplace) return null;
  const key = marketplace.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  return marketplaceToCountry[key] || marketplaceToCountry[marketplace] || null;
};

const parseMarketplaceList = (marketplace: string | null | undefined) => {
  if (!marketplace) return { countries: [] as string[], unknown: [] as string[], total: 0 };
  const ids = marketplace.split(',').map(s => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const countries: string[] = [];
  const unknown: string[] = [];
  for (const id of ids) {
    const cc = getMarketplaceCountry(id);
    if (cc && !seen.has(cc)) { seen.add(cc); countries.push(cc); }
    else if (!cc) unknown.push(id);
  }
  return { countries, unknown, total: ids.length };
};

/* ------------------------------------------------------------------ */
/*  Transaction-type normalization                                     */
/*  Amazon's flat file uses inconsistent cases ("Order", "Refund",     */
/*  "ServiceFee", "other-transaction", ...). Map them down to a small  */
/*  set of buckets so the badge color and translated label both work   */
/*  off a known key.                                                   */
/* ------------------------------------------------------------------ */

type TxBucket = 'Order' | 'Refund' | 'Fee' | 'ServiceFee' | 'Transfer' | 'Adjustment' | 'Other';

const bucketTransactionType = (raw: string | null | undefined): TxBucket => {
  if (!raw) return 'Other';
  const k = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (k === 'order' || k.startsWith('order')) return 'Order';
  if (k.includes('refund')) return 'Refund';
  if (k === 'servicefee' || k.includes('servicefee')) return 'ServiceFee';
  if (k.includes('fee')) return 'Fee';
  if (k.includes('transfer') || k.includes('payout')) return 'Transfer';
  if (k.includes('adjust')) return 'Adjustment';
  return 'Other';
};

const txBucketStyles: Record<TxBucket, { color: string; bg: string }> = {
  Order:       { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  Refund:      { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  Fee:         { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
  ServiceFee:  { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
  Transfer:    { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
  Adjustment:  { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)' },
  Other:       { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)' },
};

const TxTypeBadge: React.FC<{ rawType: string; label: string }> = ({ rawType, label }) => {
  const bucket = bucketTransactionType(rawType);
  const cfg = txBucketStyles[bucket];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
      fontWeight: 500, color: cfg.color, backgroundColor: cfg.bg, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cfg.color, display: 'inline-block', flexShrink: 0 }} />
      {label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Status helpers                                                     */
/* ------------------------------------------------------------------ */

const statusBannerColors: Record<string, string> = {
  New: 'status-new',
  Processed: 'status-success',
  ExportedToSap: 'status-info',
};

/* ------------------------------------------------------------------ */
/*  Stat tile                                                          */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, iconColor, label, value, sub }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderRadius: '10px',
    backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
    minWidth: 0,
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: iconBg, color: iconColor, fontSize: '16px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{sub}</div>
      )}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Detail page                                                        */
/* ------------------------------------------------------------------ */

const SettlementReportDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<SettlementReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TxBucket | 'all'>('all');
  // Cap initial render at PAGE_SIZE rows. Reports with 500+ transactions render
  // 4000+ DOM nodes otherwise and that's where most of the page-load lag comes from.
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const fetchReport = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await settlementReportsApi.get(id);
      setReport(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settlements.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleUpdateStatus = useCallback(async (newStatus: string) => {
    if (!id) return;
    setUpdatingStatus(true);
    try {
      await settlementReportsApi.updateStatus(id, newStatus);
      toast.success(t('settlements.statusUpdated'));
      fetchReport();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settlements.statusUpdateError'));
    } finally {
      setUpdatingStatus(false);
    }
  }, [id, fetchReport, t]);

  const handleReparse = useCallback(async () => {
    if (!id) return;
    setReparsing(true);
    try {
      const response = await settlementReportsApi.reparse(id);
      const count = response.data?.transactionCount ?? 0;
      toast.success(t('settlements.reparseSuccess', { count }));
      fetchReport();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settlements.reparseError'));
    } finally {
      setReparsing(false);
    }
  }, [id, fetchReport, t]);

  const handleDownloadCsv = useCallback(async () => {
    if (!report || !id) return;
    setDownloadingCsv(true);
    try {
      // CSV is no longer included in the detail payload — fetch on demand.
      const response = await settlementReportsApi.getCsv(id);
      const csv: string = response.data;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `settlement_${report.reportId}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settlements.fetchError'));
    } finally {
      setDownloadingCsv(false);
    }
  }, [report, id, t]);

  /* ----- formatting helpers ------------------------------------- */

  const formatCurrency = (amount: number, currency?: string | null) => {
    const cur = (currency && currency.length === 3) ? currency : 'EUR';
    try {
      return amount.toLocaleString('pl-PL', { style: 'currency', currency: cur });
    } catch {
      // Defensive: if Amazon ever sends an invalid ISO code we don't want to crash the whole page.
      return `${amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
    }
  };

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

  const formatDateTime = (dateStr: string | null) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString('pl-PL', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : null;

  const txTypeLabel = useCallback((rawType: string) => {
    const bucket = bucketTransactionType(rawType);
    const map: Record<TxBucket, string> = {
      Order: t('settlements.txTypeOrder'),
      Refund: t('settlements.txTypeRefund'),
      Fee: t('settlements.txTypeFee'),
      ServiceFee: t('settlements.txTypeServiceFee'),
      Transfer: t('settlements.txTypeTransfer'),
      Adjustment: t('settlements.txTypeAdjustment'),
      Other: t('settlements.txTypeOther'),
    };
    // If the raw type was unrecognised, surface the original string in parens so power
    // users can still see what Amazon called it.
    if (bucket === 'Other' && rawType && rawType.toLowerCase() !== 'other') {
      return `${map.Other} (${rawType})`;
    }
    return map[bucket];
  }, [t]);

  const statusLabel = useMemo(() => {
    if (!report) return '';
    const map: Record<string, string> = {
      New: t('settlements.statusNew'),
      Processed: t('settlements.statusProcessed'),
      ExportedToSap: t('settlements.statusExportedToSap'),
    };
    return map[report.status] || report.status;
  }, [report, t]);

  const orderStatusColor = report ? (statusBannerColors[report.status] || 'status-muted') : '';

  /* ----- transaction aggregates --------------------------------- */
  // Memoize so the table-render path doesn't recompute on every keystroke.
  const txStats = useMemo(() => {
    if (!report) return null;
    const buckets: Record<TxBucket, { count: number; sum: number }> = {
      Order:      { count: 0, sum: 0 },
      Refund:     { count: 0, sum: 0 },
      Fee:        { count: 0, sum: 0 },
      ServiceFee: { count: 0, sum: 0 },
      Transfer:   { count: 0, sum: 0 },
      Adjustment: { count: 0, sum: 0 },
      Other:      { count: 0, sum: 0 },
    };
    let income = 0;
    let outgoing = 0;
    for (const tx of report.transactions) {
      const b = bucketTransactionType(tx.transactionType);
      buckets[b].count += 1;
      buckets[b].sum += tx.amount;
      if (tx.amount > 0) income += tx.amount;
      else if (tx.amount < 0) outgoing += tx.amount;
    }
    return { buckets, income, outgoing, net: income + outgoing };
  }, [report]);

  // "All amounts are zero" — surface a one-click reparse button so the
  // backend bug fix can be applied to legacy data without re-fetching from Amazon.
  const allAmountsZero = useMemo(() => {
    if (!report || report.transactions.length === 0) return false;
    return report.transactions.every(tx => tx.amount === 0);
  }, [report]);

  const filteredTransactions = useMemo(() => {
    if (!report) return [] as SettlementTransaction[];
    if (typeFilter === 'all') return report.transactions;
    return report.transactions.filter(tx => bucketTransactionType(tx.transactionType) === typeFilter);
  }, [report, typeFilter]);

  // Reset pagination whenever the filter changes — otherwise scrolling to "show more"
  // and then changing filter leaves a stale offset.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [typeFilter]);

  const visibleTransactions = useMemo(
    () => filteredTransactions.slice(0, visibleCount),
    [filteredTransactions, visibleCount]
  );
  const hasMore = filteredTransactions.length > visibleTransactions.length;

  const marketplaceInfo = useMemo(
    () => parseMarketplaceList(report?.marketplace),
    [report?.marketplace]
  );

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="page-container page-container--wide">
        <div className="alert alert-danger">{t('settlements.notFound')}</div>
        <Link to="/settlements" className="btn btn-secondary">
          {t('settlements.backToList')}
        </Link>
      </div>
    );
  }

  /* ----- amount column color logic ------------------------------ */
  // Zero amounts get neutral grey; positives green; negatives red. We deliberately
  // don't recolor refund rows red as a whole — the row's transaction-type badge
  // already conveys "this is a refund", and the Amazon report sometimes encodes
  // refund principal as a positive number.
  const amountColor = (amount: number): string => {
    if (amount === 0) return 'var(--text-muted)';
    if (amount > 0) return '#10b981';
    return '#ef4444';
  };

  const formatSignedAmount = (amount: number, currency: string | null | undefined) => {
    if (amount > 0) return `+${formatCurrency(amount, currency)}`;
    return formatCurrency(amount, currency); // negatives already start with "-"
  };

  /* ----- render ------------------------------------------------- */

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/settlements" className="btn btn-link">
            <FiArrowLeft /> {t('settlements.title')}
          </Link>
          <h1>{t('settlements.reportTitle', { id: report.reportId })}</h1>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {report.hasRawCsv && (
            <button
              className="btn btn-secondary"
              onClick={handleDownloadCsv}
              disabled={downloadingCsv}
            >
              <FiDownload /> {downloadingCsv ? '...' : t('settlements.downloadCsv')}
            </button>
          )}
          {/* Re-parse button — always visible so the user can find it. If RawCsv is missing
              the backend returns a friendly error rather than parsing nothing. We give it a
              distinct orange color so it stands out as a recovery action. */}
          <button
            className="btn"
            onClick={handleReparse}
            disabled={reparsing}
            title={t('settlements.reparseHint')}
            style={{
              backgroundColor: '#f97316',
              borderColor: '#f97316',
              color: '#fff',
            }}
          >
            <FiRefreshCw style={{ animation: reparsing ? 'spin 1s linear infinite' : undefined }} />
            {reparsing ? t('settlements.reparsing') : t('settlements.reparse')}
          </button>
          {report.status === 'New' && (
            <button
              className="btn btn-primary"
              onClick={() => handleUpdateStatus('Processed')}
              disabled={updatingStatus}
            >
              <FiCheckCircle /> {t('settlements.markProcessed')}
            </button>
          )}
          {report.status === 'Processed' && (
            <button
              className="btn btn-primary"
              onClick={() => handleUpdateStatus('ExportedToSap')}
              disabled={updatingStatus}
              style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
            >
              <FiDatabase /> {t('settlements.markExportedToSap')}
            </button>
          )}
        </div>
      </div>

      {/* Status banner */}
      <div className={`po-status-banner po-${orderStatusColor}`}>
        <div className="po-status-banner-left">
          <span className={`po-status-dot po-${orderStatusColor}`} />
          <span className="po-status-label">{statusLabel}</span>
        </div>
      </div>

      {/* All-zero warning. Two flavors:
          - If we have RawCsv: actionable — offer one-click reparse.
          - If we don't: explanatory — Amazon document URLs are one-shot, so reparse can't
            help and the user just needs to wait for the next settlement report. */}
      {allAmountsZero && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', borderRadius: '10px',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          color: 'var(--text)', marginBottom: '16px',
        }}>
          <FiAlertCircle style={{ color: '#f59e0b', fontSize: '20px', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: '14px' }}>
            {report.hasRawCsv ? t('settlements.reparseHint') : t('settlements.reparseHintNoCsv')}
          </span>
          {report.hasRawCsv && (
            <button
              className="btn"
              onClick={handleReparse}
              disabled={reparsing}
              style={{
                backgroundColor: '#f59e0b',
                borderColor: '#f59e0b',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              <FiRefreshCw style={{ animation: reparsing ? 'spin 1s linear infinite' : undefined }} />
              {reparsing ? t('settlements.reparsing') : t('settlements.reparse')}
            </button>
          )}
        </div>
      )}

      {/* Detail sections */}
      <div className="po-detail-sections">

        {/* Report Info */}
        <div className="po-section">
          <div className="po-section-header">
            <FiHash className="po-section-icon" />
            <h3>{t('settlements.sectionReportInfo')}</h3>
          </div>
          <div className="po-section-grid">
            <div className="po-field">
              <span className="po-field-label">{t('settlements.reportId')}</span>
              <span className="po-field-value po-field-mono">{report.reportId}</span>
            </div>
            {report.reportType && (
              <div className="po-field">
                <span className="po-field-label">{t('settlements.reportType')}</span>
                <span className="po-field-value" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                  {report.reportType.replace(/^GET_/, '').replace(/_/g, ' ').toLowerCase()}
                </span>
              </div>
            )}
            <div className="po-field">
              <span className="po-field-label">{t('settlements.period')}</span>
              <span className="po-field-value">
                <FiCalendar className="po-field-inline-icon" />
                {formatDate(report.periodStart)} &ndash; {formatDate(report.periodEnd)}
              </span>
            </div>
            <div className="po-field" style={{ gridColumn: marketplaceInfo.total > 4 ? '1 / -1' : 'auto' }}>
              <span className="po-field-label">
                {marketplaceInfo.total > 1 ? t('settlements.marketplaces') : t('settlements.marketplace')}
              </span>
              <span className="po-field-value">
                {marketplaceInfo.total === 0 ? (
                  <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {marketplaceInfo.countries.map(cc => (
                      <span key={cc} title={cc} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 8px', borderRadius: '10px',
                        fontSize: '11px', fontWeight: 600,
                        backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
                      }}>
                        <CountryFlag countryCode={cc} size={14} />
                        {cc}
                      </span>
                    ))}
                    {marketplaceInfo.unknown.length > 0 && (
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px',
                        fontSize: '11px', fontWeight: 600,
                        backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text-muted)',
                      }} title={marketplaceInfo.unknown.join(', ')}>
                        +{marketplaceInfo.unknown.length}
                      </span>
                    )}
                  </span>
                )}
              </span>
            </div>
            <div className="po-field">
              <span className="po-field-label">{t('settlements.createdAt')}</span>
              <span className="po-field-value po-field-small">{formatDateTime(report.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="po-section">
          <div className="po-section-header">
            <FiDollarSign className="po-section-icon" />
            <h3>{t('settlements.sectionFinancial')}</h3>
          </div>
          <div className="po-section-grid">
            <div className="po-field">
              <span className="po-field-label">{t('settlements.totalAmount')}</span>
              <span className="po-field-value po-field-amount" style={{ color: amountColor(report.totalAmount) }}>
                {formatCurrency(report.totalAmount, report.currency)}
              </span>
            </div>
            <div className="po-field">
              <span className="po-field-label">{t('settlements.currency')}</span>
              <span className="po-field-value">
                {report.currency ? (
                  <span className="po-badge po-badge-currency">{report.currency}</span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                )}
              </span>
            </div>
            <div className="po-field">
              <span className="po-field-label">{t('settlements.transactionCount')}</span>
              <span className="po-field-value" style={{ fontWeight: 600 }}>
                {report.transactionCount ?? report.transactions.length ?? 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction breakdown stat tiles */}
      {txStats && report.transactions.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginTop: '16px',
        }}>
          <StatCard
            icon={<FiPackage />}
            iconBg="rgba(16, 185, 129, 0.12)"
            iconColor="#10b981"
            label={t('settlements.txStatsOrders')}
            value={txStats.buckets.Order.count}
            sub={formatCurrency(txStats.buckets.Order.sum, report.currency)}
          />
          <StatCard
            icon={<FiRefreshCw />}
            iconBg="rgba(245, 158, 11, 0.12)"
            iconColor="#f59e0b"
            label={t('settlements.txStatsRefunds')}
            value={txStats.buckets.Refund.count}
            sub={formatCurrency(txStats.buckets.Refund.sum, report.currency)}
          />
          <StatCard
            icon={<FiDollarSign />}
            iconBg="rgba(239, 68, 68, 0.12)"
            iconColor="#ef4444"
            label={t('settlements.txStatsFees')}
            value={txStats.buckets.Fee.count + txStats.buckets.ServiceFee.count}
            sub={formatCurrency(txStats.buckets.Fee.sum + txStats.buckets.ServiceFee.sum, report.currency)}
          />
          <StatCard
            icon={<FiTrendingUp />}
            iconBg="rgba(16, 185, 129, 0.12)"
            iconColor="#10b981"
            label={t('settlements.totalIncome')}
            value={formatCurrency(txStats.income, report.currency)}
          />
          <StatCard
            icon={<FiTrendingDown />}
            iconBg="rgba(239, 68, 68, 0.12)"
            iconColor="#ef4444"
            label={t('settlements.totalOutgoing')}
            value={formatCurrency(txStats.outgoing, report.currency)}
          />
        </div>
      )}

      {/* Transactions table */}
      <div style={{ marginTop: '24px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '12px', flexWrap: 'wrap', gap: '12px',
        }}>
          <h3 style={{ color: 'var(--text)', margin: 0 }}>
            {t('settlements.transactions')} ({filteredTransactions.length}
            {typeFilter !== 'all' && report.transactions.length !== filteredTransactions.length
              ? ` / ${report.transactions.length}` : ''})
          </h3>
          {report.transactions.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {(['all', 'Order', 'Refund', 'ServiceFee', 'Other'] as const).map(b => {
                const isActive = typeFilter === b;
                const count = b === 'all'
                  ? report.transactions.length
                  : (txStats?.buckets[b as TxBucket]?.count ?? 0);
                if (b !== 'all' && count === 0) return null;
                const label =
                  b === 'all' ? t('common.allTypes') :
                  b === 'Order' ? t('settlements.txTypeOrder') :
                  b === 'Refund' ? t('settlements.txTypeRefund') :
                  b === 'ServiceFee' ? t('settlements.txTypeServiceFee') :
                  t('settlements.txTypeOther');
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setTypeFilter(b)}
                    style={{
                      padding: '4px 10px', borderRadius: '12px',
                      fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      border: `1px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                      backgroundColor: isActive ? 'var(--primary)' : 'transparent',
                      color: isActive ? '#fff' : 'var(--text-muted)',
                    }}
                  >
                    {label} <span style={{ opacity: 0.7 }}>· {count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="table-responsive">
          {filteredTransactions.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>{t('settlements.noTransactions')}</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('settlements.txDate')}</th>
                  <th>{t('settlements.txType')}</th>
                  <th>{t('settlements.orderId')}</th>
                  <th>{t('settlements.sku')}</th>
                  <th>{t('settlements.description')}</th>
                  <th style={{ textAlign: 'right' }}>{t('settlements.amount')}</th>
                  <th>{t('settlements.currency')}</th>
                  <th className="col-hide-mobile">{t('settlements.marketplace')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleTransactions.map((tx: SettlementTransaction) => {
                  const cc = getMarketplaceCountry(tx.marketplace);
                  return (
                    <tr key={tx.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(tx.transactionDate)}</td>
                      <td><TxTypeBadge rawType={tx.transactionType} label={txTypeLabel(tx.transactionType)} /></td>
                      <td>
                        {tx.orderId ? (
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>
                            {tx.orderId}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                      <td>
                        {tx.sku ? (
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>
                            {tx.sku}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                      <td>
                        {tx.description ? (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}
                                title={tx.description}>
                            {tx.description.length > 50 ? tx.description.substring(0, 50) + '…' : tx.description}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>
                        <span style={{
                          fontWeight: 600,
                          color: amountColor(tx.amount),
                          whiteSpace: 'nowrap',
                        }}>
                          {formatSignedAmount(tx.amount, tx.currency)}
                        </span>
                      </td>
                      <td>
                        {tx.currency ? (
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                            fontSize: '11px', fontWeight: 600,
                            backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text)',
                          }}>
                            {tx.currency}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                      <td className="col-hide-mobile">
                        {tx.marketplace ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {cc && <CountryFlag countryCode={cc} size={14} />}
                            <span style={{ fontSize: '12px' }}>{tx.marketplace}</span>
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {hasMore && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '12px', padding: '16px',
            }}>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                {visibleTransactions.length} / {filteredTransactions.length}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              >
                +{Math.min(PAGE_SIZE, filteredTransactions.length - visibleTransactions.length)}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setVisibleCount(filteredTransactions.length)}
              >
                {t('common.total')}: {filteredTransactions.length}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettlementReportDetailPage;
